import { STATUS_CLOSED, STATUS_OPEN, STATUS_WIP, STATUS_NREL } from "./excel-constants";

/**
 * Builds Excel formulas for various calculations
 */
export class ExcelFormulaBuilder {
  /**
   * Creates fulfillment formula for a row
   * Matches the store logic: only counts non-n.rel. statuses
   */
  buildFulfillmentFormula(firstCol, lastCol, rowNum) {
    const range = `${firstCol}${rowNum}:${lastCol}${rowNum}`;
    const openCount = `COUNTIF(${range},"${STATUS_OPEN}")`;
    const wipCount = `COUNTIF(${range},"${STATUS_WIP}")`;
    const closedCount = `COUNTIF(${range},"${STATUS_CLOSED}")`;
    const totalActive = `(${openCount}+${wipCount}+${closedCount})`;

    return `=IF(${totalActive}=0,"${STATUS_NREL}",(${closedCount}+${wipCount}*0.5)/${totalActive})`;
  }

  /**
   * Creates formula for child row aggregation
   * Properly handles n.rel. and matches store logic
   */
  buildChildRowFormula(statusCol, weightCol, startRow, endRow) {
    const components = [];

    for (let i = startRow; i <= endRow; i++) {
      components.push(`
        IF(${statusCol}${i}="${STATUS_NREL}",0,
          IF(${statusCol}${i}="${STATUS_CLOSED}",${weightCol}${i},
            IF(${statusCol}${i}="${STATUS_WIP}",${weightCol}${i}*0.5,0)
          )
        )
      `);
    }

    const weightComponents = [];
    for (let i = startRow; i <= endRow; i++) {
      weightComponents.push(`IF(${statusCol}${i}="${STATUS_NREL}",0,${weightCol}${i})`);
    }

    const totalActiveWeight = `(${weightComponents.join("+")})`;
    const weightedSum = `(${components.join("+")})`;

    return `=IF(${totalActiveWeight}=0,"${STATUS_NREL}",${weightedSum}/${totalActiveWeight})`;
  }

  /**
   * Creates formula for summary row (step fulfillment)
   * This calculates the weighted average across all rows for a step
   */
  buildSummaryFormula(statusCol, weightCol, firstDataRow, lastDataRow, parentRows) {
    const components = [];

    // For each row, calculate: status_value * weight (excluding n.rel.)
    for (let row = firstDataRow; row <= lastDataRow; row++) {
      // Skip parent rows entirely to avoid double-counting their children
      if (parentRows.includes(row)) {
        components.push("0");
        continue;
      }

      components.push(`
        IF(${statusCol}${row}="${STATUS_NREL}",0,
          IF(${statusCol}${row}="${STATUS_CLOSED}",${weightCol}${row},
            IF(${statusCol}${row}="${STATUS_WIP}",${weightCol}${row}*0.5,0)
          )
        )
      `);
    }

    // Calculate total weight excluding n.rel. rows
    const weightComponents = [];
    for (let row = firstDataRow; row <= lastDataRow; row++) {
      if (parentRows.includes(row)) {
        weightComponents.push("0");
        continue;
      }
      weightComponents.push(`IF(${statusCol}${row}="${STATUS_NREL}",0,${weightCol}${row})`);
    }

    const totalActiveWeight = `(${weightComponents.join("+")})`;
    const weightedSum = `(${components.join("+")})`;

    return `=IF(${totalActiveWeight}=0,"${STATUS_NREL}",${weightedSum}/${totalActiveWeight})`;
  }

  /**
   * Creates formula for total fulfillment column
   * Calculates weighted average of all row fulfillments
   */
  buildTotalFulfillmentFormula(fulfillmentCol, weightCol, firstDataRow, lastDataRow, parentRows) {
    const components = [];
    const weightComponents = [];

    for (let row = firstDataRow; row <= lastDataRow; row++) {
      // Skip parent rows - they're already aggregated in child rows
      if (!parentRows.includes(row)) {
        components.push(`
          IF(ISNUMBER(${fulfillmentCol}${row}),
            ${fulfillmentCol}${row}*${weightCol}${row},
            0
          )
        `);

        weightComponents.push(`
          IF(ISNUMBER(${fulfillmentCol}${row}),
            ${weightCol}${row},
            0
          )
        `);
      }
    }

    const totalWeight = `(${weightComponents.join("+")})`;
    const weightedSum = `(${components.join("+")})`;

    return `=IF(${totalWeight}=0,0,${weightedSum}/${totalWeight})`;
  }
}
